import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetSegments } from '@/api/segments/useGetSegments';
import { useGetActivityLogsForMemberEmail } from '@/api/workspaces/useGetActivityLogsForMemberEmail';
import { useGetAudienceMemberById } from '@/api/workspaces/useGetAudienceMemberById';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useStripeFinancialFeaturesEnabled } from '@/hooks/useStripeFinancialFeaturesEnabled';
import {
  selectIsCollapsed,
  setCollapsedSidebar,
  setIsCollapsableIconHidden,
} from '@/component-library/layouts/SideMenu/sideMenuSlice';
import { Button } from '@/component-library/primitives/Button/Button';
// import { Button } from '@/component-library/primitives/Button/Button';
// import ChevronIcon from '@/component-library/primitives/Icons/ChevronIcon/ChevronIcon';
// import ExternalLinkIcon from '@/component-library/primitives/Icons/ExternalLinkIcon/ExternalLinkIcon';
import Loader from '@/component-library/primitives/Loader/Loader';
import MemberProfileLoader from '@/component-library/primitives/Loaders/MemberProfileLoader';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import TabMenu from '@/component-library/widgets/TabMenu/TabMenu';
import { MemberOverview } from '@/models/AudienceByWorkspace';
import { ActivityLog } from '@/models/ActivityLogsByWorkspace';
import { Segment } from '@/models/Segments';

import { useSegmentsTable } from '../../Segments/useSegmentsTable';

import ActivitiesPage from './OverviewPage/ActivitiesPage';
import InvoicesSection from './OverviewPage/InvoicesSection';
import MemberChurnHistoryTab from './OverviewPage/MemberChurnHistoryTab';
import MemberSignalsTab from './OverviewPage/MemberSignalsTab';
import MemberTicketsTab from './OverviewPage/MemberTicketsTab';
// import AttributesPage from './OverviewPage/AttributesPage';
// import useOutsideClick from '@/modules/shared/hooks/useOutsideClick';
import OverviewPage from './OverviewPage/OverviewPage';
import SegmentsSection from './OverviewPage/SegmentsSection';
import SubscriptionsSection from './OverviewPage/SubscriptionsSection';
import MemberProfileSideMenu from './MemberProfileSideMenu';

const MemberProfile: React.FC = () => {
  const { t } = useTranslation();
  const { id: memberId } = useParams<{ id: string }>();
  const { data, isFetched, isLoading } = useGetAudienceMemberById(memberId);
  const { data: memberActivityLogsData } = useGetActivityLogsForMemberEmail(data?.email);

  // Server-filtered by email so charts are not truncated by the workspace-wide activity cap.
  const memberActivityLogs: ActivityLog[] = memberActivityLogsData ?? [];

  const { data: segmentsData, isLoading: segmentsLoading } = useGetSegments();
  const { columns } = useSegmentsTable();
  const [showBelongsTo, setShowBelongsTo] = useState(true);

  const [member, setMember] = useState<MemberOverview | undefined>(undefined);
  const collapsedSidebar = useAppSelector(selectIsCollapsed);
  const [previousCollapsedState, setPreviousCollapsedState] = useState(collapsedSidebar);
  const navigate = useNavigate();

  const filteredSegments = useMemo(() => {
    const segmentValues = member?.membership?.segments?.in.map((segment) => Object.values(segment)).flat() || [];
    const belong: Segment[] = [];
    const notBelong: Segment[] = [];
    segmentsData?.forEach((segment) => {
      if (segmentValues.includes(segment.id)) {
        belong.push(segment);
      } else {
        notBelong.push(segment);
      }
    });
    return { belong, notBelong };
  }, [segmentsData, member]);

  const handleFilterClick = (belongTo: boolean) => {
    setShowBelongsTo(belongTo); // Update state to show segments that belong or don't belong
  };

  const overviewLabel = t('MemberProfilePage.overviewTabLabel') as string;
  const churnHistoryLabel = t('MemberProfilePage.churnHistoryTabLabel') as string;
  const activitiesLabel = t('MemberProfilePage.activitiesTabLabel') as string;
  const signalsLabel = t('MemberProfilePage.signalsTabLabel') as string;
  const ticketsLabel = t('MemberProfilePage.ticketsTabLabel') as string;
  const stripeFinancial = useStripeFinancialFeaturesEnabled();

  const tabLabels: string[] = useMemo(() => {
    const base = [overviewLabel, churnHistoryLabel, activitiesLabel, signalsLabel, ticketsLabel, 'User Groups'];
    if (stripeFinancial) {
      base.push('Subscriptions', 'Invoices');
    }
    return base;
  }, [stripeFinancial, overviewLabel, churnHistoryLabel, activitiesLabel, signalsLabel, ticketsLabel]);

  const [activeStep, setActiveStep] = useState<string>(overviewLabel);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!stripeFinancial && (activeStep === 'Subscriptions' || activeStep === 'Invoices')) {
      setActiveStep(overviewLabel);
    }
  }, [stripeFinancial, activeStep, overviewLabel]);

  useEffect(() => {
    // dispatch(setCollapsedSidebar(true));
    // dispatch(setIsCollapsableIconHidden(true));
    isFetched && setMember(data);
    // return () => {
    //   !previousCollapsedState && dispatch(setCollapsedSidebar(false));
    //   dispatch(setIsCollapsableIconHidden(false));
    // };
  }, [dispatch, isFetched]);

  const changeActiveStep = (step: string) => {
    setActiveStep(step);
  };

  return (
    <div>
      {isLoading ? (
        <MemberProfileLoader />
      ) : (
        <>
          <MemberProfileSideMenu memberData={data} activityLogs={memberActivityLogs} />

          <div>
            <TabMenu tabLabels={tabLabels} activeStep={activeStep} changeActiveStep={changeActiveStep} />
            <div className="mt-6">
              {activeStep === overviewLabel && (
                <OverviewPage
                  memberData={data}
                  activityLogs={memberActivityLogs}
                  changeActiveStep={changeActiveStep}
                  segmentsData={filteredSegments}
                  activitiesTabLabel={activitiesLabel}
                />
              )}
              {activeStep === churnHistoryLabel && <MemberChurnHistoryTab memberData={data} />}
              {/* {activeStep === t('MemberProfilePage.attributesTabLabel') && (
                <AttributesPage memberData={member?.attributes} changeActiveStep={changeActiveStep} />
              )} */}
              {activeStep === activitiesLabel && (
                <ActivitiesPage data={memberActivityLogs} changeActiveStep={changeActiveStep} />
              )}
              {activeStep === signalsLabel && <MemberSignalsTab memberData={data} />}
              {activeStep === ticketsLabel && <MemberTicketsTab memberData={data} />}
              {activeStep === 'User Groups' && <SegmentsSection segmentsData={segmentsData ?? []} memberData={data} />}
              {activeStep === 'Subscriptions' && (
                <SubscriptionsSection subscriptions={(data as any)?.subscriptions || []} />
              )}
              {activeStep === 'Invoices' && <InvoicesSection invoices={(data as any)?.invoices || []} />}

              {/* {activeStep === t('MemberProfilePage.devicesTabLabel') && (
                <div className="h-[100vh]">
                  <h2 className="text-gray-50 text-base font-normal tracking-[0.32px]">
                    {t('MemberProfilePage.devicesTabLabel')}
                  </h2>
                </div>
              )} */}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MemberProfile;
